import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, Plus, RefreshCw, Trophy } from "lucide-react";
import { toast } from "sonner";

const POSITIONS = [
  { key: "winner", label: "Winner" },
  { key: "runner_up", label: "Runner-up" },
  { key: "third", label: "3rd Place" },
  { key: "fourth", label: "4th Place" },
  { key: "qf", label: "Quarter-finalist" },
  { key: "pre_qf", label: "Pre Quarter-finalist" },
  { key: "r32", label: "Round of 32" },
  { key: "group_3rd", label: "3rd in Group" },
  { key: "group_4th", label: "4th in Group" },
  { key: "group_5th", label: "5th in Group" },
  { key: "group_6th", label: "6th in Group" },
];

const EVENT_CATEGORIES = [
  { key: "mens_singles", label: "Men's Singles" },
  { key: "mens_doubles", label: "Men's Doubles" },
  { key: "womens_singles", label: "Women's Singles" },
  { key: "womens_doubles", label: "Women's Doubles" },
  { key: "mixed_doubles", label: "Mixed Doubles" },
];
const DOUBLES_CATEGORIES = new Set(["mens_doubles", "womens_doubles", "mixed_doubles"]);

const AGES = ["u_10", "u_12", "u_14", "u_16", "u_19", "19+", "35+", "50+", "60+"];

export default function AdminResultsPanel({ tournaments, players }) {
  const [tid, setTid] = useState("");
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ player_id: "", event_category: "mens_singles", age_category: "19+", position: "winner", partner_player_id: "" });
  const [loading, setLoading] = useState(false);
  const [playerSearch, setPlayerSearch] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterAge, setFilterAge] = useState("all");

  const filteredPlayers = useMemo(() => {
    const q = playerSearch.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => (`${p.name || ""} ${p.district || ""} ${p.jpa_id || ""}`).toLowerCase().includes(q));
  }, [players, playerSearch]);
  const filteredPartners = useMemo(() => {
    const q = partnerSearch.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => (`${p.name || ""} ${p.district || ""} ${p.jpa_id || ""}`).toLowerCase().includes(q));
  }, [players, partnerSearch]);

  const filteredRows = useMemo(() => rows.filter((r) => (filterEvent === "all" || r.event_category === filterEvent) && (filterAge === "all" || r.age_category === filterAge)), [rows, filterEvent, filterAge]);

  const tournament = useMemo(() => tournaments.find((t) => t.id === tid), [tournaments, tid]);
  const playerNameById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p.name])), [players]);

  const loadResults = async (id) => {
    if (!id) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/admin/tournaments/${id}/results`);
      setRows(r.data);
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadResults(tid); }, [tid]);

  const addRow = () => {
    if (!draft.player_id) { toast.error("Select a player"); return; }
    setRows((prev) => [
      ...prev.filter((r) => !(r.player_id === draft.player_id && r.event_category === draft.event_category && r.age_category === draft.age_category)),
      {
        id: `pending-${Date.now()}`,
        tournament_id: tid,
        player_id: draft.player_id,
        event_category: draft.event_category,
        age_category: draft.age_category,
        position: draft.position,
        partner_player_id: draft.partner_player_id || null,
        player_name: playerNameById[draft.player_id],
        partner_name: draft.partner_player_id ? playerNameById[draft.partner_player_id] : null,
        _pending: true,
      },
    ]);
    setDraft({ ...draft, player_id: "", partner_player_id: "" });
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const saveAll = async () => {
    if (!tid) return;
    const payload = {
      results: rows.map((r) => ({
        player_id: r.player_id,
        event_category: r.event_category,
        age_category: r.age_category,
        position: r.position,
        partner_player_id: r.partner_player_id || null,
      })),
    };
    try {
      const r = await api.post(`/admin/tournaments/${tid}/results`, payload);
      const skipped = r.data.skipped || [];
      if (skipped.length) {
        toast.warning(`Saved ${r.data.results_written} results · Skipped ${skipped.length} (${skipped.slice(0, 2).map((s) => s.reason).join(", ")}${skipped.length > 2 ? "…" : ""})`);
      } else {
        toast.success(`Saved ${r.data.results_written} results · ${r.data.rankings_written} rankings updated`);
      }
      await loadResults(tid);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const recomputeAll = async () => {
    try {
      const r = await api.post("/admin/rankings/recompute");
      toast.success(`Recomputed · ${r.data.rankings_written} ranking rows written`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recompute failed");
    }
  };

  const recomputeTournament = async () => {
    if (!tid) { toast.error("Select a tournament first"); return; }
    try {
      const r = await api.post(`/admin/tournaments/${tid}/recompute-results`);
      const d = r.data;
      toast.success(`Rebuilt from completed brackets · ${d.categories_with_medals.length} categories with medals${d.categories_pending.length ? ` · ${d.categories_pending.length} still pending (finals/pools not finished)` : ""}`);
      await loadResults(tid);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recompute failed");
    }
  };

  const doublesNeedsPartner = DOUBLES_CATEGORIES.has(draft.event_category);

  return (
    <div className="border border-jpa-border rounded-sm p-6" data-testid="admin-results-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5 text-jpa-primary" />
          <h3 className="font-display text-2xl tracking-wider">Enter Tournament Results</h3>
        </div>
        <Button data-testid="admin-recompute-btn" onClick={recomputeAll} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">
          <RefreshCw className="w-4 h-4 mr-2" /> Recompute all rankings
        </Button>
        <Button data-testid="admin-recompute-tournament-btn" onClick={recomputeTournament} disabled={!tid} className="rounded-sm uppercase tracking-widest text-xs font-bold bg-jpa-primary text-white hover:bg-jpa-primary-hover">
          <Trophy className="w-4 h-4 mr-2" /> Recompute THIS tournament
        </Button>
      </div>
      <p className="text-sm text-jpa-muted mb-5">
        Assign finish positions to players. Points are auto-calculated per AIPA policy (Tier × Position),
        and player rankings are recomputed instantly.
      </p>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label>Tournament</Label>
          <Select value={tid} onValueChange={setTid}>
            <SelectTrigger data-testid="admin-results-tournament" className="mt-1 rounded-sm"><SelectValue placeholder="Select a tournament" /></SelectTrigger>
            <SelectContent>{tournaments.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name} · {t.status} · {t.division}
                {t.is_annual_ranking ? " · Annual" : ""}
                {t.is_national ? " · National" : ""}
              </SelectItem>
            ))}</SelectContent>
          </Select>
          {tournament && (
            <div className="mt-2 text-xs text-jpa-muted space-y-0.5">
              <div><b>Division:</b> {tournament.division || "—"} · <b>Tier:</b> {tournament.tier ?? "—"} · <b>JPA Sanctioned:</b> {String(!!tournament.jpa_sanctioned)}</div>
              <div><b>Annual Ranking:</b> {String(!!tournament.is_annual_ranking)} · <b>Approval:</b> {tournament.approval_status || "—"}</div>
              {!tournament.jpa_sanctioned && (
                <div className="text-jpa-live font-bold mt-1">This tournament is NOT JPA-sanctioned — results will be recorded but will NOT affect JPA rankings.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {tid && (
        <>
          <div className="mt-6 grid md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label>Player</Label>
              <Input data-testid="admin-results-player-search" placeholder="Search by name, district, or JPA ID…" value={playerSearch} onChange={(e) => setPlayerSearch(e.target.value)} className="mt-1 rounded-sm h-9" />
              <Select value={draft.player_id} onValueChange={(v) => setDraft({ ...draft, player_id: v })}>
                <SelectTrigger data-testid="admin-results-player" className="mt-1 rounded-sm"><SelectValue placeholder={filteredPlayers.length ? `Select from ${filteredPlayers.length} player(s)` : "No matching players"} /></SelectTrigger>
                <SelectContent className="max-h-72">{filteredPlayers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · {p.district} · {p.gender}{p.jpa_id ? ` · ${p.jpa_id}` : ""}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event category</Label>
              <Select value={draft.event_category} onValueChange={(v) => setDraft({ ...draft, event_category: v })}>
                <SelectTrigger data-testid="admin-results-event" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_CATEGORIES.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Age category</Label>
              <Select value={draft.age_category} onValueChange={(v) => setDraft({ ...draft, age_category: v })}>
                <SelectTrigger data-testid="admin-results-age" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish position</Label>
              <Select value={draft.position} onValueChange={(v) => setDraft({ ...draft, position: v })}>
                <SelectTrigger data-testid="admin-results-position" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{POSITIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {doublesNeedsPartner && (
              <div className="md:col-span-2">
                <Label>Partner (doubles/mixed)</Label>
                <Input data-testid="admin-results-partner-search" placeholder="Search partner…" value={partnerSearch} onChange={(e) => setPartnerSearch(e.target.value)} className="mt-1 rounded-sm h-9" />
                <Select value={draft.partner_player_id} onValueChange={(v) => setDraft({ ...draft, partner_player_id: v })}>
                  <SelectTrigger data-testid="admin-results-partner" className="mt-1 rounded-sm"><SelectValue placeholder="Select partner (optional)" /></SelectTrigger>
                  <SelectContent className="max-h-72">{filteredPartners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} · {p.district}{p.jpa_id ? ` · ${p.jpa_id}` : ""}</SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button data-testid="admin-results-add" onClick={addRow} className="mt-4 bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Add result
          </Button>

          <div className="mt-6 border border-jpa-border">
            <div className="grid md:grid-cols-3 gap-3 p-3 border-b border-jpa-border bg-jpa-surface/40">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Filter by event</Label>
                <Select value={filterEvent} onValueChange={setFilterEvent}>
                  <SelectTrigger data-testid="admin-results-filter-event" className="mt-1 rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {EVENT_CATEGORIES.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Filter by age</Label>
                <Select value={filterAge} onValueChange={setFilterAge}>
                  <SelectTrigger data-testid="admin-results-filter-age" className="mt-1 rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ages</SelectItem>
                    {AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end text-xs text-jpa-muted">
                Showing <b className="mx-1">{filteredRows.length}</b> of <b className="mx-1">{rows.length}</b> result rows
              </div>
            </div>
            <div className="grid grid-cols-12 bg-jpa-ink text-white py-2 px-3 text-[10px] font-bold uppercase tracking-widest">
              <div className="col-span-3">Player</div>
              <div className="col-span-2">Event</div>
              <div className="col-span-2">Age</div>
              <div className="col-span-2">Position</div>
              <div className="col-span-2">Partner</div>
              <div className="col-span-1 text-right">Del</div>
            </div>
            {loading ? (
              <div className="py-10 text-center text-jpa-muted text-sm">Loading…</div>
            ) : filteredRows.length === 0 ? (
              <div className="py-10 text-center text-jpa-muted text-sm">{rows.length === 0 ? "No results yet. Add players above." : "No rows match the current filter."}</div>
            ) : (
              filteredRows.map((r) => (
                <div key={r.id} data-testid={`admin-results-row-${r.id}`} className={`grid grid-cols-12 items-center py-2 px-3 border-t border-jpa-border text-sm ${r._pending ? "bg-amber-50" : "bg-white"}`}>
                  <div className="col-span-3 font-bold truncate">{r.player_name || playerNameById[r.player_id] || r.player_id}</div>
                  <div className="col-span-2">{EVENT_CATEGORIES.find((e) => e.key === r.event_category)?.label || r.event_category}</div>
                  <div className="col-span-2">{r.age_category}</div>
                  <div className="col-span-2">{POSITIONS.find((p) => p.key === r.position)?.label || r.position}</div>
                  <div className="col-span-2 truncate">{r.partner_name || (r.partner_player_id ? playerNameById[r.partner_player_id] : "—")}</div>
                  <div className="col-span-1 text-right">
                    <button data-testid={`admin-results-delete-${r.id}`} onClick={() => removeRow(r.id)} className="text-jpa-live hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-jpa-muted">
              Saving replaces all results for this tournament and auto-recomputes rankings.
            </div>
            <Button data-testid="admin-results-save" onClick={saveAll} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
              Save results & recompute rankings
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
